/**
 * GuideDetailModal
 * Headless UI dialog that displays a text guide.
 * - Fragment HTML (from CMS): rendered as sanitized HTML via DOMPurify.
 * - Full HTML documents (training guides): rendered in a sandboxed iframe.
 */

import { Fragment, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import { OathDualSpinner } from '../../../shared/OathUI';

const MAX_IFRAME_HEIGHT = 'calc(90vh - 80px)';

/** Detect whether the body is a full HTML document (has doctype or html tag). */
const checkIsFullHtml = (html) => {
  if (!html) return false;
  const trimmed = html.trimStart().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
};

/**
 * GuideDetailModal component
 * @param {Object} props
 * @param {Object|null} props.resource - Resource to display (null = closed)
 * @param {string|null} props.body - Full HTML body content (may load async)
 * @param {boolean} props.loadingBody - True while fetching full body
 * @param {Function} props.onClose - Close handler
 */
const GuideDetailModal = ({ resource, body, loadingBody, onClose }) => {
  const isOpen = !!resource;
  const rawBody = body ?? resource?.body;

  const isFullHtml = useMemo(() => checkIsFullHtml(rawBody), [rawBody]);

  // --- Fragment HTML path (existing behavior) ---
  const sanitizedBody = useMemo(() => {
    if (isFullHtml || !rawBody) return null;
    return DOMPurify.sanitize(rawBody);
  }, [rawBody, isFullHtml]);

  // --- Full HTML path (ported from TrainingGuideModal) ---
  const [iframeHeight, setIframeHeight] = useState(null);

  const processedHtml = useMemo(() => {
    if (!isFullHtml || !rawBody) return null;

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const baseTag = origin ? `<base href="${origin}/">` : '';
    let html = rawBody;

    if (baseTag && !/<base\s/i.test(html)) {
      html = html.includes('<head>')
        ? html.replace('<head>', `<head>${baseTag}`)
        : `${baseTag}${html}`;
    }

    const anchorScript = `<script>document.addEventListener('click',function(e){var a=e.target.closest('a[href^="#"]');if(a){e.preventDefault();var t=document.getElementById(a.getAttribute('href').slice(1));if(t)t.scrollIntoView({behavior:'smooth'});}});</script>`;
    return html.includes('</body>')
      ? html.replace('</body>', anchorScript + '</body>')
      : html + anchorScript;
  }, [rawBody, isFullHtml]);

  const handleIframeLoad = useCallback((e) => {
    try {
      const doc = e.target.contentDocument;
      if (doc?.body) setIframeHeight(doc.body.scrollHeight);
    } catch {
      setIframeHeight(null);
    }
  }, []);

  // Intercept TOC anchor clicks for fragment HTML so they scroll within the modal
  const handleContentClick = useCallback((e) => {
    const anchor = e.target.closest('a[href^="#"]');
    if (anchor) {
      e.preventDefault();
      const targetId = anchor.getAttribute('href').slice(1);
      try {
        const target = e.currentTarget.querySelector(`[id="${CSS.escape(targetId)}"]`);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      } catch { /* ignore invalid selectors */ }
    }
  }, []);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </TransitionChild>

        {/* Modal panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={`w-full ${isFullHtml ? 'max-w-5xl max-h-[90vh]' : 'max-w-2xl max-h-[85vh]'} bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col`}
              >
                {/* Orange accent bar */}
                <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400 flex-shrink-0" />

                {/* Header */}
                <div className="flex items-start justify-between p-4 sm:p-5 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                      <DocumentTextIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white leading-snug">
                        {resource?.title}
                      </DialogTitle>
                      {resource?.categoryName && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {resource.categoryName}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="ml-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                    aria-label="Close guide"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                {isFullHtml ? (
                  /* Full HTML document → sandboxed iframe */
                  <div className="flex-1 overflow-hidden">
                    {loadingBody ? (
                      <div className="flex flex-col items-center justify-center py-20 gap-3">
                        <OathDualSpinner size="lg" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading guide...</p>
                      </div>
                    ) : (
                      <iframe
                        key={resource?.resourceId}
                        srcDoc={processedHtml}
                        title={resource?.title}
                        sandbox="allow-scripts allow-same-origin"
                        className="w-full border-0"
                        style={{
                          height: iframeHeight ? `${iframeHeight}px` : MAX_IFRAME_HEIGHT,
                          maxHeight: MAX_IFRAME_HEIGHT,
                        }}
                        onLoad={handleIframeLoad}
                      />
                    )}
                  </div>
                ) : (
                  /* Fragment HTML → DOMPurify + inline render */
                  <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {loadingBody ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <OathDualSpinner size="md" />
                        <p className="text-sm text-gray-400 dark:text-gray-500">Loading guide...</p>
                      </div>
                    ) : sanitizedBody ? (
                      <div
                        className="prose prose-sm prose-gray dark:prose-invert max-w-none
                          prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-white
                          prose-p:text-gray-600 dark:prose-p:text-gray-300
                          prose-a:text-primary dark:prose-a:text-primary-light
                          prose-strong:text-gray-800 dark:prose-strong:text-gray-200
                          prose-li:text-gray-600 dark:prose-li:text-gray-300
                          prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:rounded prose-code:px-1"
                        onClick={handleContentClick}
                        dangerouslySetInnerHTML={{ __html: sanitizedBody }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400 dark:text-gray-500">
                        <DocumentTextIcon className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" />
                        <p className="text-sm">No content available for this guide.</p>
                      </div>
                    )}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GuideDetailModal;
